import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePayroll } from '../hooks/usePayroll';
import { useShiftManager } from '../hooks/useShiftManager';
import { db, functions } from '../services/firebase';
import { collection, getDocs, orderBy, query, where, Timestamp, doc, onSnapshot } from 'firebase/firestore';
import EmployeeManager from '../components/EmployeeManager';
import AppSettings from '../components/AppSettings';
// Importar funciones de formato y utilidades de zona horaria desde utils/formatters.js
import {
  formatCurrency,
  formatDuration,
  formatDateInPR,
  getCurrentPRTime, // Importar getCurrentPRTime
  utcToPuertoRicoTime, // Importar utcToPuertoRicoTime
  createPRDateTime // Importar createPRDateTime para manejar correctamente las fechas en PR
} from '../utils/formatters'; 
import {
  LogOut, Terminal, Loader2, Users, Filter, Clock, Tag, Plus, Edit, X,
  Download, Eye, ChevronDown, ChevronUp, FileSpreadsheet, BarChart3,
  TrendingUp, Activity, Calendar, DollarSign, ShoppingBag, Zap,
  Calculator, Code
} from 'lucide-react';
import { format as formatDateFns } from 'date-fns'; // Renombrado para no confundir con formatDateInPR
import { httpsCallable } from 'firebase/functions'; //

// NOTA: Las funciones de zona horaria y el offset ya están en formatters.js
// Eliminamos las duplicadas de aquí:
// const PR_TIMEZONE_OFFSET = -4; 
// const utcToPuertoRicoTime = (utcDate) => { ... };
// const puertoRicoTimeToUtc = (prDate) => { ... };
// const getCurrentPRTime = () => { ... };

// === SIMULACIÓN TEMPORAL DE useToast ===
const useToast = () => {
  const showToast = (type, message) => {
    console.log(`[Toast ${type.toUpperCase()}]: ${message}`);
  };
  return { showToast };
};

// === COMPONENTE MODAL PARA CREAR/EDITAR TURNOS ===
const ShiftEditorModal = ({ isOpen, onClose, shift, employees, onSave, preselectedEmployeeId }) => {
  const shiftManager = useShiftManager();
  const { loading, error, success, resetStatus } = shiftManager;
  const { showToast } = useToast();
  
  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');

  const isEditing = shift !== null;

  // Inicializar campos solo cuando el modal se abre
  useEffect(() => {
    if (isOpen) {
      if (isEditing && shift) {
        // Al editar un turno existente:
        // Convertir el Timestamp UTC de Firestore a la hora local de PR para mostrar en los inputs
        const startPR = utcToPuertoRicoTime(shift.startTime.toDate()); // Usar la función importada
        const endPR = utcToPuertoRicoTime(shift.endTime.toDate()); // Usar la función importada

        setEmployeeId(shift.employeeId);
        setStartDate(formatDateFns(startPR, 'yyyy-MM-dd'));
        setStartTime(formatDateFns(startPR, 'HH:mm'));
        setEndDate(formatDateFns(endPR, 'yyyy-MM-dd'));
        setEndTime(formatDateFns(endPR, 'HH:mm'));
      } else {
        // Al crear un nuevo turno:
        // Inicializar con la fecha/hora actual en la zona horaria de PR
        const nowPR = getCurrentPRTime(); // Usar la función importada
        setEmployeeId(preselectedEmployeeId || employees[0]?.id || '');
        setStartDate(formatDateFns(nowPR, 'yyyy-MM-dd'));
        setStartTime(formatDateFns(nowPR, 'HH:mm'));
        setEndDate(formatDateFns(nowPR, 'yyyy-MM-dd'));
        setEndTime(formatDateFns(nowPR, 'HH:mm'));
      }
    }
    // Solo ejecutar cuando isOpen cambia (cuando el modal se abre/cierra)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (success) {
      showToast('success', `¡Turno ${isEditing ? 'actualizado' : 'creado'} correctamente!`);
      const timer = setTimeout(() => {
        onClose();
        resetStatus();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success, onClose, resetStatus, isEditing, showToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!employeeId) {
      showToast('error', 'Por favor seleccione un empleado.');
      return;
    }
    
    // Crear objetos Date a partir de las cadenas de input.
    // Estos objetos Date se crearán en la zona horaria LOCAL del navegador.
    // useShiftManager.js ya se encarga de convertir esto a PR_TIMEZONE y luego a UTC para Firestore.
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    
    if (endDateTime <= startDateTime) {
      showToast('error', 'La hora de fin debe ser posterior a la hora de inicio.');
      return;
    }
    
    const selectedEmployee = employees.find(emp => emp.id === employeeId);

    const shiftData = {
      employeeId,
      employeeName: selectedEmployee?.name || 'Empleado Desconocido',
      startTime: startDateTime, 
      endTime: endDateTime,     
      hourlyRate: selectedEmployee?.hourlyRate || 10.50,
    };
    
    let successResult = false;
    try {
      if (isEditing) {
        successResult = await shiftManager.updateShift(shift.id, shiftData);
      } else {
        successResult = await shiftManager.createShift(shiftData);
      }
      
      if (successResult) {
        onSave(shiftData);
      }
    } catch (err) {
      showToast('error', `Error al guardar turno: ${err.message || 'Desconocido'}`);
    }
  };

  const handleClose = () => {
    resetStatus();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-900 border border-white/20 rounded-2xl p-8 w-full max-w-lg relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition"
        >
          <X size={24} />
        </button>
        <h2 className="text-2xl font-bold mb-6 text-white">
          {isEditing ? 'Editar Turno' : 'Crear Nuevo Turno'}
        </h2>
        
        {success ? (
          <div className="text-center py-10">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <p className="text-green-400 text-lg font-semibold">
                ¡Turno {isEditing ? 'actualizado' : 'creado'} correctamente!
              </p>
              <p className="text-white/60 text-sm mt-2 mb-4">
                El reporte se actualizará automáticamente...
              </p>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-green-600 rounded-lg text-white hover:bg-green-700 transition"
              >
                Cerrar
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Empleado</label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                disabled={isEditing}
                className="w-full bg-white/10 border border-white/20 rounded-lg py-3 px-4 focus:ring-2 focus:ring-white/50 focus:outline-none disabled:opacity-50 text-white"
                required
              >
                <option value="">Seleccionar empleado...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Inicio</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)} 
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 mb-2 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
                <input 
                  type="time" 
                  value={startTime} 
                  onChange={e => setStartTime(e.target.value)} 
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Fin</label>
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={e => setEndDate(e.target.value)} 
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 mb-2 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
                <input 
                  type="time" 
                  value={endTime} 
                  onChange={e => setEndTime(e.target.value)} 
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                  required
                />
              </div>
            </div>
            
            {error && (
              <div className="text-red-400 text-sm text-center bg-red-500/10 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading || !employeeId} 
              className="w-full py-3 bg-green-600 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center text-white"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                isEditing ? 'Actualizar Turno' : 'Crear Turno'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// === COMPONENTE PARA DESGLOSE DETALLADO DE TURNOS ===
const DetailedShiftsModal = ({ isOpen, onClose, employee, shifts, onEditShift }) => {
  if (!isOpen || !employee) return null;

  const totalHours = shifts.reduce((acc, shift) => acc + (shift.durationMinutes / 60), 0);
  const totalEarnings = shifts.reduce((acc, shift) => acc + (shift.earnings || 0), 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/20 rounded-2xl p-8 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Turnos Detallados - {employee.employeeName}</h2>
            <p className="text-white/60 mt-1">
              {shifts.length} turnos • {formatDuration(totalHours)} • {formatCurrency(totalEarnings)}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white transition"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="flex-1 overflow-auto">
          <div className="space-y-3">
            {shifts.map((shift, index) => (
              <div key={shift.id || index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="text-lg font-semibold text-white">
                        Turno #{index + 1}
                      </span>
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                        {shift.status === 'completed' ? 'Completado' : 'Activo'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-white/60">Fecha</p>
                        {/* Usar formatDateInPR para mostrar la fecha en la zona horaria de PR */}
                        <p className="font-medium text-white">
                          {formatDateInPR(shift.startTime, 'dd/MM/yyyy')}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-white/60">Hora de Entrada</p>
                        {/* Se cambia el formato de la hora de 'HH:mm' a 'hh:mm a' para mostrar el horario de 12 horas */}
                        <p className="font-medium text-white">
                          {formatDateInPR(shift.startTime, 'hh:mm a')}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-white/60">Hora de Salida</p>
                        {/* Se cambia el formato de la hora de 'HH:mm' a 'hh:mm a' para mostrar el horario de 12 horas */}
                        <p className="font-medium text-white">
                          {shift.endTime ? formatDateInPR(shift.endTime, 'hh:mm a') : 'En progreso'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-white/60">Duración</p>
                        <p className="font-medium text-white">
                          {formatDuration(shift.durationMinutes / 60)}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-white/60">Tarifa/Hora</p>
                        <p className="font-medium text-white">
                          {formatCurrency(employee.hourlyRate || 10.50)}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-white/60">Ganancias</p>
                        <p className="font-medium text-green-400">
                          {formatCurrency(shift.earnings || 0)}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-white/60">Creado</p>
                        {/* Usar formatDateInPR para mostrar la hora de creación en la zona horaria de PR */}
                        <p className="font-medium text-xs text-white">
                          {formatDateInPR(shift.createdAt, 'dd/MM hh:mm a')}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-white/60">Modificado</p>
                        {/* Usar formatDateInPR para mostrar la hora de modificación en la zona horaria de PR */}
                        <p className="font-medium text-xs text-white">
                          {shift.updatedAt ? formatDateInPR(shift.updatedAt, 'dd/MM hh:mm a') : 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    {shift.editedBy && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs text-white/50">
                          Editado por: {shift.editedBy}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => onEditShift(shift)}
                      className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-lg transition"
                      title="Editar turno"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="mt-6 pt-4 border-t border-white/20">
          <div className="flex justify-between items-center">
            <div className="text-sm text-white/60">
              Total de {shifts.length} turnos en el período seleccionado
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition text-white"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE PARA ESTADÍSTICAS EJECUTIVAS ===
const ExecutiveDashboard = ({ reportSummary, channelStats, periodStats, reportMetadata, isVisible, onToggle }) => {
  if (!isVisible) {
    return (
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8">
        <button 
          onClick={onToggle}
          className="w-full flex items-center justify-between p-4 hover:bg-white/5 rounded-lg transition"
        >
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-white/70" />
            <h3 className="text-lg font-semibold text-white">Dashboard Ejecutivo</h3>
          </div>
          <ChevronDown className="w-5 h-5 text-white/50" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-white/70" />
          <h3 className="text-lg font-semibold text-white">Dashboard Ejecutivo</h3>
        </div>
        <button 
          onClick={onToggle}
          className="p-2 hover:bg-white/10 rounded-lg transition"
        >
          <ChevronUp className="w-5 h-5 text-white/50" />
        </button>
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-white/60 text-sm">Total a Pagar</p>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(reportSummary?.totalToPay || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-8 h-8 text-blue-400" />
            <div>
              <p className="text-white/60 text-sm">Horas Totales</p>
              <p className="text-2xl font-bold text-white">
                {formatDuration(reportSummary?.totalHours || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-8 h-8 text-purple-400" />
            <div>
              <p className="text-white/60 text-sm">Comisiones</p>
              <p className="text-2xl font-bold text-purple-400">
                {formatCurrency(reportSummary?.totalCommissions || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 text-orange-400" />
            <div>
              <p className="text-white/60 text-sm">Empleados Activos</p>
              <p className="text-2xl font-bold text-white">
                {reportSummary?.totalEmployees || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown por Canal */}
      {channelStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
              <ShoppingBag className="w-5 h-5" />
              Breakdown por Canal
            </h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-white">Punto de Venta (POS)</p>
                  <p className="text-sm text-white/60">{channelStats.pos.orders} órdenes</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-white">{formatCurrency(channelStats.pos.sales)}</p>
                  <p className="text-sm text-white/60">{channelStats.pos.percentage}%</p>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-white">Tienda Online</p>
                  <p className="text-sm text-white/60">{channelStats.web.orders} órdenes</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-white">{formatCurrency(channelStats.web.sales)}</p>
                  <p className="text-sm text-white/60">{channelStats.web.percentage}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown Financiero Detallado */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
              <DollarSign className="w-5 h-5" />
              Breakdown Financiero Detallado
            </h4>
            <div className="space-y-3 text-sm">
              {reportMetadata?.financialBreakdown ? (
                <>
                  <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span className="text-white/80">Ventas Brutas</span>
                    <span className="font-bold text-green-400">
                      {formatCurrency(reportMetadata.financialBreakdown.grossSales)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Descuentos</span>
                    <span className="font-bold text-red-400">
                      -{formatCurrency(reportMetadata.financialBreakdown.discounts)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Devoluciones</span>
                    <span className="font-bold text-red-400">
                      -{formatCurrency(reportMetadata.financialBreakdown.refunds)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span className="text-white/80 font-semibold">Ventas Netas</span>
                    <span className="font-bold text-blue-400">
                      {formatCurrency(reportMetadata.financialBreakdown.netSales)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Impuestos</span>
                    <span className="font-bold text-yellow-400">
                      +{formatCurrency(reportMetadata.financialBreakdown.taxes)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/80">Envíos</span>
                    <span className="font-bold text-purple-400">
                      +{formatCurrency(reportMetadata.financialBreakdown.shipping)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t-2 border-white/20 pt-2 mt-2">
                    <span className="text-white font-semibold">Total Cobrado</span>
                    <span className="font-bold text-xl text-green-400">
                      {formatCurrency(reportMetadata.financialBreakdown.totalSales)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-white/60 text-xs">Órdenes Procesadas</p>
                      <p className="font-bold text-white">{reportMetadata.financialBreakdown.ordersCount}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs">Valor Promedio</p>
                      <p className="font-bold text-white">{formatCurrency(reportMetadata.financialBreakdown.averageOrderValue)}</p>
                    </div>
                    {reportMetadata.financialBreakdown.refundedOrders > 0 && (
                      <div>
                        <p className="text-white/60 text-xs">Órdenes con Devoluciones</p>
                        <p className="font-bold text-red-400">{reportMetadata.financialBreakdown.refundedOrders}</p>
                      </div>
                    )}
                    {reportMetadata.financialBreakdown.cancelledOrders > 0 && (
                      <div>
                        <p className="text-white/60 text-xs">Órdenes Canceladas</p>
                        <p className="font-bold text-red-400">{reportMetadata.financialBreakdown.cancelledOrders}</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-white/60 text-center py-4">No hay datos financieros disponibles</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// === COMPONENTE PRINCIPAL ===
const AdminReportPage = () => {
  const { logout, navigateToTerminal } = useAuth();
  const { showToast } = useToast();
  const {
    loading,
    error,
    reportData,
    reportPeriod,
    reportSummary,
    reportMetadata,
    channelBreakdown,
    calculateCustomReport,
    exportCurrentReport,
    exportEmployeeShifts,
    exportSalesReport,
    getChannelStatistics,
    getPeriodStatistics,
    canExport,
    exportInProgress,
    exportProgress,
    clearShopifyData
  } = usePayroll();

  // ── New modals ──
  const [showEmployeeManager, setShowEmployeeManager] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [oauthBanner, setOauthBanner] = useState(null);

  // Load company logo
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'company'), snap => {
      if (snap.exists()) setCompanyLogo(snap.data().logoUrl || null);
    });
    return unsub;
  }, []);

  // Handle OAuth callback from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get('oauth');
    const shop = params.get('shop');
    const msg = params.get('msg');
    if (oauth === 'success') {
      setOauthBanner({ type: 'success', text: `✓ ${shop} conectado correctamente` });
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setOauthBanner(null), 5000);
    } else if (oauth === 'error') {
      setOauthBanner({ type: 'error', text: `Error OAuth: ${msg || 'Error desconocido'}` });
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setOauthBanner(null), 7000);
    }
  }, []);

  // Estados locales
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [reportContentType, setReportContentType] = useState('all'); // Faltaba esta declaración
  
  // Estados para modales
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [selectedEmployeeForModal, setSelectedEmployeeForModal] = useState(null);
  const [detailedShiftsModal, setDetailedShiftsModal] = useState({ isOpen: false, employee: null, shifts: [] });
  
  // Estados para UI
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showExecutiveDashboard, setShowExecutiveDashboard] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false); 

  // Estados para filtros
  // Ahora, inicializamos los estados de fecha/hora para que reflejen la hora actual de PR
  const nowPR = getCurrentPRTime(); // Usar la función importada de formatters.js
  const [startDate, setStartDate] = useState(formatDateFns(nowPR, 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState(formatDateFns(nowPR, 'HH:mm'));
  const [endDate, setEndDate] = useState(formatDateFns(nowPR, 'yyyy-MM-dd'));
  const [endTime, setEndTime] = useState(formatDateFns(nowPR, 'HH:mm')); // Puede ser 23:59 por defecto para fin de día

  // Estados para filtros de Shopify
  const [financialStatus, setFinancialStatus] = useState('any');
  const [fulfillmentStatus, setFulfillmentStatus] = useState('any');
  const [excludeTestOrders, setExcludeTestOrders] = useState(false);
  const [excludeDraftOrders, setExcludeDraftOrders] = useState(false);

  // ✅ FUNCIÓN PARA OBTENER EMPLEADOS DESDE FIREBASE
  const fetchEmployees = useCallback(async () => {
    try {
      const q = query(collection(db, 'sellers'), orderBy('name'));
      const snapshot = await getDocs(q);
      setEmployees(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('Error fetching employees:', err);
      showToast('error', 'Error al cargar la lista de empleados.');
    }
  }, [showToast]);

  // ✅ FUNCIÓN PARA OBTENER TURNOS DETALLADOS DESDE FIREBASE
  const fetchDetailedShifts = useCallback(async (employeeId, startDateTime, endDateTime) => {
    try {
      // Ampliar el rango de búsqueda 24 horas hacia atrás para capturar turnos
      // que empezaron antes pero terminan dentro del rango seleccionado
      const searchStartDate = new Date(startDateTime.getTime() - 24 * 60 * 60 * 1000);

      const shiftsQuery = query(
        collection(db, 'shifts'),
        where('employeeId', '==', employeeId),
        where('startTime', '>=', Timestamp.fromDate(searchStartDate)),
        where('startTime', '<=', Timestamp.fromDate(endDateTime)),
        orderBy('startTime', 'desc')
      );

      const snapshot = await getDocs(shiftsQuery);
      const allShifts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filtrar turnos que se superponen con el rango seleccionado
      const filteredShifts = allShifts.filter(shift => {
        const shiftStart = shift.startTime?.toDate();
        const shiftEnd = shift.endTime?.toDate();

        if (!shiftStart || !shiftEnd) {
          return false;
        }

        // Verificar si hay superposición entre el turno y el rango solicitado
        const overlaps = shiftStart < endDateTime && shiftEnd > startDateTime;
        return overlaps;
      });

      console.log(`📥 Turnos detallados: ${allShifts.length} consultados, ${filteredShifts.length} filtrados`);
      return filteredShifts;
    } catch (err) {
      console.error('Error fetching detailed shifts:', err);
      showToast('error', 'Error al cargar los turnos detallados.');
      return [];
    }
  }, [showToast]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleGenerateCustomReport = useCallback(async () => {
    if (startDate && endDate && startTime && endTime) {
      // Crear objetos Date interpretando las fechas/horas como Puerto Rico time (UTC-4)
      // Usamos createPRDateTime para asegurar que las fechas se interpreten correctamente
      const startDateTime = createPRDateTime(startDate, startTime);
      const endDateTime = createPRDateTime(endDate, endTime);

      if (endDateTime < startDateTime) {
        showToast('error', 'La fecha y hora de fin no puede ser anterior a la de inicio.');
        return;
      }

      const reportOptions = {
        includeHours: reportContentType === 'all' || reportContentType === 'hours',
        includeCommissions: reportContentType === 'all' || reportContentType === 'commissions',
        financialStatus,
        fulfillmentStatus,
        excludeTestOrders,
        excludeDraftOrders
      };
      
      showToast('info', 'Generando reporte... Esto puede tardar unos segundos.');
      await calculateCustomReport(employees, startDateTime, endDateTime, reportOptions);
      // El showToast de éxito se maneja dentro de calculateCustomReport en usePayroll
      // o se puede añadir aquí si prefieres un control más granular.
    } else {
      showToast('error', "Por favor, complete todas las fechas y horas.");
    }
  }, [startDate, startTime, endDate, endTime, reportContentType, employees, calculateCustomReport, showToast]);

  const handleExportFullReport = async () => {
    try {
      showToast('info', 'Preparando exportación del reporte completo. Se descargarán múltiples archivos CSV.');
      const result = await exportCurrentReport({
        filename: `reporte_completo_${startDate}_${endDate}`,
        includeShiftsDetail: true,
        includeSalesDetail: true,
        includeExecutiveSummary: true
      });
      
      if (result.success) {
        showToast('success', `Exportación completada. ${result.message}`);
      } else {
        showToast('error', `Error al exportar: ${result.error}`);
      }
    } catch (error) {
      showToast('error', `Error inesperado al exportar: ${error.message}`);
    }
  };

  const handleExportSales = async () => {
    try {
      showToast('info', 'Preparando exportación de ventas. Se descargarán múltiples archivos CSV.');
      const result = await exportSalesReport({
        filename: `ventas_unificadas_${startDate}_${endDate}`,
        includeOrderDetails: true,
        includeChannelBreakdown: true
      });
      
      if (result.success) {
        showToast('success', `Exportación completada. ${result.message}`);
      } else {
        showToast('error', `Error al exportar: ${result.error}`);
      }
    } catch (error) {
      showToast('error', `Error inesperado al exportar: ${error.message}`);
    }
  };

  const handleShowDetailedShifts = async (employee) => {
    // Al pasar fechas a fetchDetailedShifts, se pasan como objetos Date locales
    // Firestore los convierte a Timestamp UTC correctamente.
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    
    if (endDateTime < startDateTime) {
      showToast('error', 'El rango de fechas del reporte no es válido. Ajuste las fechas para ver los turnos detallados.');
      return;
    }

    showToast('info', `Cargando turnos de ${employee.employeeName}...`);
    const shifts = await fetchDetailedShifts(employee.employeeId, startDateTime, endDateTime);
    
    if (shifts.length === 0) {
      showToast('info', `No se encontraron turnos para ${employee.employeeName} en el período seleccionado.`);
      setDetailedShiftsModal({ isOpen: false, employee: null, shifts: [] });
      return;
    }

    setDetailedShiftsModal({
      isOpen: true,
      employee,
      shifts
    });
  };

  const handleOpenCreateModal = () => {
    setEditingShift(null);
    setSelectedEmployeeForModal(null);
    setIsShiftModalOpen(true);
  };

  const handleOpenCreateModalForEmployee = (employeeId) => {
    setEditingShift(null);
    setSelectedEmployeeForModal(employeeId);
    setIsShiftModalOpen(true);
  };

  const handleOpenEditModal = (shift) => {
    setEditingShift(shift);
    setSelectedEmployeeForModal(null);
    setIsShiftModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsShiftModalOpen(false);
    setEditingShift(null);
    setSelectedEmployeeForModal(null);
  };

  const handleShiftSaved = () => {
    setTimeout(() => {
      handleGenerateCustomReport();
    }, 1000);
  };

  const toggleRowExpansion = (employeeId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedRows(newExpanded);
  };

  const filteredData = reportData && selectedEmployee !== 'all'
    ? reportData.filter(item => {
        return item.employeeId === selectedEmployee;
      })
    : reportData;

  const channelStats = getChannelStatistics();
  const periodStats = getPeriodStatistics();

  const handleRecalculateEarnings = async () => {
    setRecalculating(true);
    showToast('info', 'Iniciando recalculo de ganancias de turnos antiguos. Esto puede tardar.');
    try {
      const { httpsCallable } = await import('firebase/functions');
      const recalculateFunction = httpsCallable(functions, 'recalculateShiftEarnings');
      const result = await recalculateFunction();
      
      if (result.data && result.data.success) {
        showToast('success', `Recálculo completado: ${result.data.message}`);
        handleGenerateCustomReport(); 
      } else {
        showToast('error', `Falló el recálculo: ${result.data?.message || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error("Error al invocar la función de recálculo:", err);
      showToast('error', `Error crítico al recalcular: ${err.message}`);
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">

        {/* OAuth banner */}
        {oauthBanner && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm flex items-center justify-between ${oauthBanner.type === 'success' ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-300' : 'bg-red-500/15 border border-red-500/30 text-red-300'}`}>
            {oauthBanner.text}
            <button onClick={() => setOauthBanner(null)} className="ml-4 text-white/30 hover:text-white">✕</button>
          </div>
        )}

        {/* Header */}
        <header className="flex justify-between items-center mb-10 w-full">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <img
              src={companyLogo || '/LogoNibpr.png'}
              alt="Logo"
              className={`h-10 flex-shrink-0 ${!companyLogo ? 'filter brightness-0 invert' : 'object-contain rounded'}`}
            />
            <h1 className="text-xl sm:text-2xl font-bold truncate">Panel de Administración</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Employees */}
            <button onClick={() => setShowEmployeeManager(true)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/40 rounded-lg transition text-sm backdrop-blur-sm">
              <Users size={16} />
              <span className="hidden sm:inline">Empleados</span>
            </button>
            {/* Settings */}
            <button onClick={() => setShowSettings(true)}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition text-sm backdrop-blur-sm">
              ⚙️
              <span className="hidden sm:inline">Config</span>
            </button>
            {/* Recalculate (keep for now) */}
            <button onClick={handleRecalculateEarnings} disabled={recalculating}
              className="flex items-center gap-2 px-3 py-2 bg-yellow-600/20 rounded-lg hover:bg-yellow-600/40 transition text-sm backdrop-blur-sm disabled:opacity-50"
              title="Recalcular ganancias">
              {recalculating ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />}
              <span className="hidden lg:inline">{recalculating ? 'Recalculando...' : 'Recalcular'}</span>
            </button>
            <button onClick={navigateToTerminal}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition text-sm backdrop-blur-sm">
              <Terminal size={16} />
              <span className="hidden sm:inline">Terminal</span>
            </button>
            <button onClick={logout} className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/40 transition backdrop-blur-sm">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Modals */}
        <EmployeeManager isOpen={showEmployeeManager} onClose={() => setShowEmployeeManager(false)} />
        <AppSettings isOpen={showSettings} onClose={() => setShowSettings(false)} />

        {/* Dashboard Ejecutivo */}
        <ExecutiveDashboard
          reportSummary={reportSummary}
          channelStats={channelStats}
          periodStats={periodStats}
          reportMetadata={reportMetadata}
          isVisible={showExecutiveDashboard}
          onToggle={() => setShowExecutiveDashboard(!showExecutiveDashboard)}
        />

        {/* Filtrar por Empleado */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-white/70 flex-shrink-0" />
            <h3 className="text-lg font-semibold">Filtrar por Empleado</h3>
          </div>
          
          <div className="flex gap-3 overflow-x-auto pb-2">
            <label className="cursor-pointer flex-shrink-0">
              <input
                type="radio"
                name="employee"
                value="all"
                checked={selectedEmployee === 'all'}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="sr-only"
              />
              <div className={`px-4 py-2 rounded-full border-2 transition ${
                selectedEmployee === 'all' 
                  ? 'bg-blue-600 border-blue-600 font-semibold' 
                  : 'bg-white/10 border-transparent hover:border-white/40'
              }`}>
                Todos
              </div>
            </label>
            
            {employees.map((emp) => (
              <label key={emp.id} className="cursor-pointer flex-shrink-0">
                <input
                  type="radio"
                  name="employee"
                  value={emp.id}
                  checked={selectedEmployee === emp.id}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="sr-only"
                />
                <div className={`px-4 py-2 rounded-full border-2 transition ${
                  selectedEmployee === emp.id 
                    ? 'bg-blue-600 border-blue-600 font-semibold' 
                    : 'bg-white/10 border-transparent hover:border-white/40'
                }`}>
                  {emp.name}
                </div>
              </label>
            ))}
          </div>
          
          {employees.length > 3 && (
            <div className="text-xs text-white/50 mt-2 text-center">
              ← Desliza horizontalmente para ver más empleados →
            </div>
          )}
        </div>

        {/* Reporte Personalizado */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-6 h-6 text-white/70" />
            <h3 className="text-lg font-semibold">Reporte Personalizado</h3>
          </div>
          
          <div className="mb-6">
            <p className="text-sm font-medium text-white/70 mb-3">Seleccionar tipo de datos a incluir:</p>
            <div className="flex flex-wrap gap-3">
              {['all', 'hours', 'commissions'].map(type => (
                <label key={type} className="cursor-pointer">
                  <input
                    type="radio"
                    name="reportContentType"
                    value={type}
                    checked={reportContentType === type}
                    onChange={(e) => setReportContentType(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`px-4 py-2 rounded-full border-2 transition flex items-center gap-2 ${
                    reportContentType === type
                      ? 'bg-green-600 border-green-600 font-semibold'
                      : 'bg-white/10 border-transparent hover:border-white/40'
                  }`}>
                    {type === 'all' && 'Ambos'}
                    {type === 'hours' && <><Clock size={16}/> Horas</>}
                    {type === 'commissions' && <><Tag size={16}/> Comisiones</>}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Filtros Avanzados de Shopify */}
          <div className="mb-6 border border-white/10 rounded-lg p-4 bg-white/5">
            <p className="text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
              <ShoppingBag size={16} />
              Filtros Avanzados de Shopify
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Financial Status */}
              <div>
                <label className="block text-xs text-white/60 mb-2">Estado Financiero</label>
                <select
                  value={financialStatus}
                  onChange={(e) => setFinancialStatus(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                >
                  <option value="any">Cualquiera</option>
                  <option value="paid">Pagadas</option>
                  <option value="pending">Pendientes</option>
                  <option value="refunded">Reembolsadas</option>
                  <option value="partially_paid">Parcialmente Pagadas</option>
                  <option value="partially_refunded">Parcialmente Reembolsadas</option>
                  <option value="voided">Anuladas</option>
                </select>
              </div>

              {/* Fulfillment Status */}
              <div>
                <label className="block text-xs text-white/60 mb-2">Estado de Envío</label>
                <select
                  value={fulfillmentStatus}
                  onChange={(e) => setFulfillmentStatus(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 text-sm focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                >
                  <option value="any">Cualquiera</option>
                  <option value="fulfilled">Completadas (con envío)</option>
                  <option value="unfulfilled">Pendientes de envío</option>
                  <option value="partial">Parcialmente enviadas</option>
                  <option value="null">Sin envío (POS/local)</option>
                </select>
              </div>

              {/* Checkboxes para exclusiones */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludeTestOrders}
                    onChange={(e) => setExcludeTestOrders(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-2 focus:ring-white/50"
                  />
                  <span className="text-sm text-white/80">Excluir órdenes de prueba</span>
                </label>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludeDraftOrders}
                    onChange={(e) => setExcludeDraftOrders(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-2 focus:ring-white/50"
                  />
                  <span className="text-sm text-white/80">Excluir borradores</span>
                </label>
              </div>
            </div>

            {/* Preset para replicar Shopify Admin */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs text-white/60 mb-2">Presets rápidos:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setFinancialStatus('paid');
                    setFulfillmentStatus('any');
                    setExcludeTestOrders(false);
                    setExcludeDraftOrders(false);
                    showToast('info', 'Filtro: Solo órdenes pagadas (incluye POS)');
                  }}
                  className="px-3 py-2 bg-green-600/20 border border-green-500/50 rounded-lg hover:bg-green-600/40 transition text-xs flex items-center gap-2"
                >
                  <DollarSign size={14} />
                  Solo Pagadas
                </button>
                <button
                  onClick={() => {
                    setFinancialStatus('paid');
                    setFulfillmentStatus('fulfilled');
                    setExcludeTestOrders(false);
                    setExcludeDraftOrders(false);
                    showToast('info', 'Filtro: Pagadas + Completadas (solo órdenes web)');
                  }}
                  className="px-3 py-2 bg-blue-600/20 border border-blue-500/50 rounded-lg hover:bg-blue-600/40 transition text-xs flex items-center gap-2"
                >
                  <ShoppingBag size={14} />
                  Pagadas + Completadas
                </button>
                <button
                  onClick={() => {
                    setFinancialStatus('any');
                    setFulfillmentStatus('any');
                    setExcludeTestOrders(false);
                    setExcludeDraftOrders(false);
                    showToast('info', 'Filtros desactivados: Todas las órdenes');
                  }}
                  className="px-3 py-2 bg-gray-600/20 border border-gray-500/50 rounded-lg hover:bg-gray-600/40 transition text-xs flex items-center gap-2"
                >
                  <X size={14} />
                  Sin Filtros
                </button>
              </div>
            </div>
          </div>
          
          {/* Botones de rango rápido */}
          <div className="mb-4">
            <label className="text-sm font-medium text-white/70 mb-2 block">Atajos de Rango</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const now = getCurrentPRTime();
                  setStartDate(formatDateFns(now, 'yyyy-MM-dd'));
                  setStartTime('00:00');
                  setEndDate(formatDateFns(now, 'yyyy-MM-dd'));
                  setEndTime('23:59');
                }}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition text-sm"
              >
                Hoy
              </button>
              <button
                onClick={() => {
                  const now = getCurrentPRTime();
                  const dayOfWeek = now.getDay();
                  const startOfWeek = new Date(now);
                  startOfWeek.setDate(now.getDate() - dayOfWeek);
                  setStartDate(formatDateFns(startOfWeek, 'yyyy-MM-dd'));
                  setStartTime('00:00');
                  setEndDate(formatDateFns(now, 'yyyy-MM-dd'));
                  setEndTime('23:59');
                }}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition text-sm"
              >
                Esta Semana
              </button>
              <button
                onClick={() => {
                  const now = getCurrentPRTime();
                  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                  setStartDate(formatDateFns(startOfMonth, 'yyyy-MM-dd'));
                  setStartTime('00:00');
                  setEndDate(formatDateFns(now, 'yyyy-MM-dd'));
                  setEndTime('23:59');
                }}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition text-sm"
              >
                Mes Hasta Hoy
              </button>
              <button
                onClick={() => {
                  const now = getCurrentPRTime();
                  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                  setStartDate(formatDateFns(startOfMonth, 'yyyy-MM-dd'));
                  setStartTime('00:00');
                  setEndDate(formatDateFns(endOfMonth, 'yyyy-MM-dd'));
                  setEndTime('23:59');
                }}
                className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition text-sm"
              >
                Mes Completo
              </button>
              <button
                onClick={() => {
                  clearShopifyData();
                  showToast('success', 'Caché purgado. Genera un nuevo reporte para actualizar los datos.');
                }}
                className="px-4 py-2 bg-red-600/80 border border-red-500/50 rounded-lg hover:bg-red-600 transition text-sm flex items-center gap-2"
                title="Purgar caché y forzar actualización de datos de Shopify"
              >
                <Zap size={16} />
                Purgar Caché
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium text-white/70">Desde</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                />
                <input
                  type="time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-white/70">Hasta</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                />
                <input
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  className="bg-white/10 border border-white/20 rounded-lg py-2 px-3 focus:ring-2 focus:ring-white/50 focus:outline-none text-white"
                />
              </div>
            </div>
            
            <div className="flex gap-4 w-full">
              <button 
                onClick={handleGenerateCustomReport} 
                disabled={loading} 
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Filter size={18} />}
                Generar Reporte
              </button>
            </div>
          </div>
        </div>

        {/* Resultados y Exportaciones */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 min-h-[400px]">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-bold">Resultados</h2>
            <div className="flex gap-2">
              <button 
                onClick={handleExportFullReport}
                disabled={!canExport || exportInProgress}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 rounded-lg font-semibold hover:bg-green-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportInProgress ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet size={16} />
                )}
                Exportar CSV
              </button>
              <button
                onClick={handleExportSales}
                disabled={!canExport || exportInProgress}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg font-semibold hover:bg-blue-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                Exportar Ventas (CSV)
              </button>
              <button
                onClick={() => setShowDebugModal(true)}
                disabled={!reportData || reportData.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 rounded-lg font-semibold hover:bg-yellow-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title="Ver datos completos del reporte en formato JSON"
              >
                <Code size={16} />
                Debug JSON
              </button>
              <button
                onClick={handleOpenCreateModal}
                disabled={employees.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 rounded-lg font-semibold hover:bg-purple-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} />
                Añadir Turno
              </button>
            </div>
          </div>
          <p className="text-white/60 mb-6">{reportPeriod || "Seleccione un reporte para empezar."}</p>
          
          {loading && (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-white/50"/>
            </div>
          )}
          
          {error && (
            <div className="text-red-400 text-center p-4 bg-red-500/10 rounded-lg">{error}</div>
          )}
          
          {filteredData && filteredData.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-4 font-semibold">Empleado</th>
                    {reportContentType !== 'commissions' && <th className="text-right py-3 px-4 font-semibold">Horas</th>}
                    {reportContentType !== 'commissions' && <th className="text-right py-3 px-4 font-semibold">Pago Horas</th>}
                    {reportContentType !== 'hours' && <th className="text-right py-3 px-4 font-semibold">Ventas Netas</th>}
                    {reportContentType !== 'hours' && <th className="text-right py-3 px-4 font-semibold">Órdenes</th>}
                    {reportContentType !== 'hours' && <th className="text-right py-3 px-4 font-semibold">Comisiones</th>}
                    {reportContentType === 'all' && <th className="text-right py-3 px-4 font-semibold">Total a Pagar</th>}
                    <th className="text-right py-3 px-4 font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map(item => (
                    <React.Fragment key={item.employeeId}>
                      <tr className="border-b border-white/10 hover:bg-white/5 transition">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleRowExpansion(item.employeeId)}
                              className="p-1 hover:bg-white/10 rounded transition"
                            >
                              {expandedRows.has(item.employeeId) ? 
                                <ChevronUp size={16} /> : 
                                <ChevronDown size={16} />
                              }
                            </button>
                            <span className="font-medium">{item.employeeName}</span>
                          </div>
                        </td>
                        {reportContentType !== 'commissions' && <td className="text-right py-3 px-4">{formatDuration(item.totalHours)}</td>}
                        {reportContentType !== 'commissions' && <td className="text-right py-3 px-4">{formatCurrency(item.totalEarnings)}</td>}
                        {reportContentType !== 'hours' && <td className="text-right py-3 px-4">{formatCurrency(item.sales)}</td>}
                        {reportContentType !== 'hours' && <td className="text-right py-3 px-4">{item.orders}</td>}
                        {reportContentType !== 'hours' && (
                          <td className="text-right py-3 px-4">
                            <div>{formatCurrency(item.commission)}</div>
                            <div className="text-[10px] text-white/40">ganancia: {formatCurrency(item.commissionBase ?? 0)}</div>
                          </td>
                        )}
                        {reportContentType === 'all' && <td className="text-right py-3 px-4 font-bold text-green-400">{formatCurrency(item.totalToPay)}</td>}
                        <td className="text-right py-3 px-4">
                          <div className="flex justify-end gap-2">
                            {/* Si hay 1 turno: botón de edición directa */}
                            {item.canEdit && item.editableShift && (
                              <button
                                onClick={() => handleOpenEditModal(item.editableShift)}
                                className="p-2 text-blue-400 hover:text-blue-300 transition"
                                title="Editar turno"
                              >
                                <Edit size={16} />
                              </button>
                            )}

                            {/* Si hay múltiples turnos: mostrar badge y botón para ver detalles */}
                            {item.shiftsCount > 1 && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400 px-2 py-1 bg-gray-800 rounded">
                                  {item.shiftsCount} turnos
                                </span>
                                <button
                                  onClick={() => handleShowDetailedShifts(item)}
                                  className="p-2 text-blue-400 hover:text-blue-300 transition"
                                  title="Ver y editar turnos"
                                >
                                  <Eye size={16} />
                                </button>
                              </div>
                            )}

                            {/* Si no tiene turnos pero sí comisiones: botón para agregar turno */}
                            {item.totalHours === 0 && item.commission > 0 && (
                              <button
                                onClick={() => handleOpenCreateModalForEmployee(item.employeeId)}
                                className="p-2 text-green-400 hover:text-green-300 transition"
                                title="Agregar turno para este empleado"
                              >
                                <Plus size={16} />
                              </button>
                            )}
                            
                            {item.totalHours === 0 && item.commission === 0 && (
                              <span className="text-xs text-gray-500 px-2 py-1">
                                Sin actividad
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Fila expandida con información adicional */}
                      {expandedRows.has(item.employeeId) && (
                        <tr className="bg-white/5">
                          <td colSpan="8" className="py-4 px-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-white/60 mb-1">Información del Empleado</p>
                                <p>ID: {item.employeeId}</p>
                                <p>Tarifa: {formatCurrency(item.hourlyRate || 10.50)}/hora</p>
                                <p>Promedio turno: {formatDuration(item.averageShiftHours || 0)}</p>
                              </div>
                              {item.orderDetails && item.orderDetails.length > 0 && (
                                <div>
                                  <p className="text-white/60 mb-1">Información de Ventas</p>
                                  <p>Órdenes Legacy: {item.legacyOrders || 0}</p>
                                  <p>Órdenes Modernas: {item.modernOrders || 0}</p>
                                  <p>Promedio por orden: {formatCurrency(item.averageOrderValue || 0)}</p>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleShowDetailedShifts(item)}
                                  className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded text-xs hover:bg-blue-500/30"
                                >
                                  Ver Turnos
                                </button>
                                {item.shiftsCount > 0 && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const result = await exportEmployeeShifts(item.employeeId);
                                        if (result.success) {
                                          showToast('success', `✅ ${result.message}`);
                                        }
                                      } catch (error) {
                                        showToast('error', `❌ Error: ${error.message}`);
                                      }
                                    }}
                                    className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30"
                                  >
                                    Exportar Turnos
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredData && filteredData.length === 0 && (
            <div className="text-center py-10">
              <p className="text-white/60">No se encontraron datos para el período seleccionado.</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Gestión de Turnos */}
      <ShiftEditorModal 
        isOpen={isShiftModalOpen}
        onClose={handleCloseModal}
        shift={editingShift}
        employees={employees}
        onSave={handleShiftSaved}
        preselectedEmployeeId={selectedEmployeeForModal}
      />

      {/* Modal de Turnos Detallados */}
      <DetailedShiftsModal
        isOpen={detailedShiftsModal.isOpen}
        onClose={() => setDetailedShiftsModal({ isOpen: false, employee: null, shifts: [] })}
        employee={detailedShiftsModal.employee}
        shifts={detailedShiftsModal.shifts}
        onEditShift={handleOpenEditModal}
      />

      {/* Modal de Debug JSON */}
      {showDebugModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <Code className="w-6 h-6 text-yellow-400" />
                <h3 className="text-xl font-bold text-white">Debug: Datos Completos del Reporte</h3>
              </div>
              <button
                onClick={() => setShowDebugModal(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="space-y-6">
                {/* Análisis de Discrepancia de Órdenes */}
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    🔍 Análisis de Discrepancia: ¿Por qué 43 órdenes vs 33 en Shopify?
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white/5 p-3 rounded">
                        <p className="text-white/60 text-xs">Órdenes desde API</p>
                        <p className="font-bold text-xl text-white">{reportMetadata?.totalOrdersFetched || 0}</p>
                      </div>
                      <div className="bg-white/5 p-3 rounded">
                        <p className="text-white/60 text-xs">Después filtros Shopify</p>
                        <p className="font-bold text-xl text-yellow-400">{reportMetadata?.totalOrdersProcessed || 0}</p>
                      </div>
                      <div className="bg-white/5 p-3 rounded">
                        <p className="text-white/60 text-xs">Después filtro tiempo</p>
                        <p className="font-bold text-xl text-green-400">{reportMetadata?.financialBreakdown?.ordersCount || 0}</p>
                      </div>
                      <div className="bg-white/5 p-3 rounded">
                        <p className="text-white/60 text-xs">Órdenes filtradas</p>
                        <p className="font-bold text-xl text-red-400">
                          {(reportMetadata?.totalOrdersFetched || 0) - (reportMetadata?.financialBreakdown?.ordersCount || 0)}
                        </p>
                      </div>
                    </div>

                    {/* Análisis de Estados */}
                    {(() => {
                      const allOrders = reportData?.flatMap(emp => emp.orderDetails || []) || [];
                      const financialStatuses = {};
                      const fulfillmentStatuses = {};
                      const cancelledCount = allOrders.filter(o => o.cancelledAt).length;

                      allOrders.forEach(order => {
                        const fs = order.financialStatus || 'null';
                        const ffs = order.fulfillmentStatus || 'null';
                        financialStatuses[fs] = (financialStatuses[fs] || 0) + 1;
                        fulfillmentStatuses[ffs] = (fulfillmentStatuses[ffs] || 0) + 1;
                      });

                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div className="bg-white/5 p-3 rounded">
                            <p className="text-white font-semibold mb-2 text-xs">Estados Financieros (de {allOrders.length} órdenes visibles)</p>
                            <div className="space-y-1 text-xs">
                              {Object.entries(financialStatuses).map(([status, count]) => (
                                <div key={status} className="flex justify-between">
                                  <span className="text-white/70">{status}</span>
                                  <span className="font-bold text-blue-400">{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-white/5 p-3 rounded">
                            <p className="text-white font-semibold mb-2 text-xs">Estados de Envío (de {allOrders.length} órdenes visibles)</p>
                            <div className="space-y-1 text-xs">
                              {Object.entries(fulfillmentStatuses).map(([status, count]) => (
                                <div key={status} className="flex justify-between">
                                  <span className="text-white/70">{status === 'null' ? 'Sin envío (POS)' : status}</span>
                                  <span className="font-bold text-purple-400">{count}</span>
                                </div>
                              ))}
                              {cancelledCount > 0 && (
                                <div className="flex justify-between pt-2 border-t border-white/10">
                                  <span className="text-red-400">Canceladas</span>
                                  <span className="font-bold text-red-400">{cancelledCount}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 mt-3">
                      <p className="text-yellow-400 font-semibold text-xs mb-1">💡 Posible explicación de la discrepancia:</p>
                      <ul className="text-xs text-white/80 space-y-1 ml-4 list-disc">
                        <li><strong>10-15 órdenes</strong> pueden haber sido creadas antes/después del rango de tiempo exacto (diferencias de timezone UTC vs UTC-4)</li>
                        <li><strong>Shopify Admin</strong> probablemente muestra solo órdenes <code>paid</code> + excluye canceladas</li>
                        <li><strong>Filtro de tiempo cliente</strong>: {(reportMetadata?.totalOrdersProcessed || 0) - (reportMetadata?.financialBreakdown?.ordersCount || 0)} órdenes fuera del rango horario exacto</li>
                      </ul>
                    </div>

                    {/* Información Detallada de Órdenes Excluidas */}
                    {reportMetadata?.excludedOrders && reportMetadata.excludedOrders.length > 0 && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 mt-3">
                        <p className="text-blue-400 font-semibold text-xs mb-2">
                          📋 Órdenes Excluidas por Filtro de Tiempo ({reportMetadata.excludedOrders.length} órdenes)
                        </p>
                        <div className="max-h-60 overflow-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-gray-900">
                              <tr className="border-b border-white/10">
                                <th className="text-left py-1 px-2 text-white/70">Orden</th>
                                <th className="text-left py-1 px-2 text-white/70">Vendedor</th>
                                <th className="text-left py-1 px-2 text-white/70">Fecha UTC</th>
                                <th className="text-left py-1 px-2 text-white/70">Hora PR</th>
                                <th className="text-right py-1 px-2 text-white/70">Total</th>
                                <th className="text-left py-1 px-2 text-white/70">Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportMetadata.excludedOrders.map((order, idx) => {
                                const orderDateUTC = new Date(order.orderDate);
                                // Usar orderDatePR si está disponible (nuevo), sino calcular (compatibilidad)
                                const orderDatePR = order.orderDatePR
                                  ? new Date(order.orderDatePR)
                                  : new Date(orderDateUTC.getTime() + (-4 * 60 * 60 * 1000)); // UTC-4 para PR

                                return (
                                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                    <td className="py-1 px-2 font-mono text-blue-400">{order.orderNumber}</td>
                                    <td className="py-1 px-2 text-white/80">{order.seller}</td>
                                    <td className="py-1 px-2 text-white/60 font-mono text-[10px]">
                                      {orderDateUTC.toISOString().split('T')[0]} {orderDateUTC.toISOString().split('T')[1].substring(0, 8)}
                                    </td>
                                    <td className="py-1 px-2 text-yellow-400 font-mono text-[10px]">
                                      {orderDatePR.toISOString().split('T')[0]} {orderDatePR.toISOString().split('T')[1].substring(0, 8)}
                                    </td>
                                    <td className="py-1 px-2 text-right text-green-400">${order.total}</td>
                                    <td className="py-1 px-2 text-white/60 text-[10px]">{order.reason}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resumen */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Resumen General
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-white/60">Empleados</p>
                      <p className="font-bold text-white">{reportData?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-white/60">Total a Pagar</p>
                      <p className="font-bold text-green-400">{formatCurrency(reportSummary?.totalToPay || 0)}</p>
                    </div>
                    <div>
                      <p className="text-white/60">Horas Totales</p>
                      <p className="font-bold text-blue-400">{(reportSummary?.totalHours || 0).toFixed(2)}h</p>
                    </div>
                    <div>
                      <p className="text-white/60">Comisiones</p>
                      <p className="font-bold text-purple-400">{formatCurrency(reportSummary?.totalCommissions || 0)}</p>
                    </div>
                  </div>
                </div>

                {/* Breakdown Financiero */}
                {reportMetadata?.financialBreakdown && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Breakdown Financiero (Shopify)
                    </h4>
                    <pre className="text-xs text-white/80 overflow-auto bg-black/30 p-4 rounded">
                      {JSON.stringify(reportMetadata.financialBreakdown, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Datos Completos del Reporte */}
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      Datos Completos del Reporte
                    </h4>
                    <button
                      onClick={() => {
                        const dataStr = JSON.stringify({
                          reportData,
                          reportMetadata,
                          reportSummary,
                          channelBreakdown,
                          reportPeriod
                        }, null, 2);
                        navigator.clipboard.writeText(dataStr);
                        showToast('success', 'JSON copiado al portapapeles');
                      }}
                      className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded transition"
                    >
                      Copiar JSON
                    </button>
                  </div>
                  <pre className="text-xs text-white/80 overflow-auto bg-black/30 p-4 rounded max-h-96">
                    {JSON.stringify({
                      reportPeriod,
                      reportData,
                      reportMetadata,
                      reportSummary,
                      channelBreakdown
                    }, null, 2)}
                  </pre>
                </div>

                {/* Metadata Raw */}
                {reportMetadata && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Metadata Completo
                    </h4>
                    <pre className="text-xs text-white/80 overflow-auto bg-black/30 p-4 rounded max-h-64">
                      {JSON.stringify(reportMetadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 bg-white/5 flex justify-between items-center text-xs text-white/60">
              <p>Usa este JSON para comparar con los datos de Shopify y encontrar discrepancias</p>
              <button
                onClick={() => setShowDebugModal(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition text-white"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReportPage;
